import { createApi } from "@reduxjs/toolkit/query/react";

import type { IApiResponse } from "@/shared/interfaces/api-response.interface";

import { ApiTags } from "../api-tags.enum";
import customFetchBase from "../custom-fetch-base";

import type {
  IMapSettingPreset,
  IMapSettingPresetListResponse,
  ICreateMapSettingPresetRequest,
  IGetMapSettingPresetsParams,
  IUpdateMapSettingPresetRequest,
  IUserMapSettingPreset,
} from "./map-setting-presets.interface";

const mapPresetListTag = (districtId: string) =>
  ({ type: ApiTags.MapSettingPresets, id: `LIST-${districtId}` }) as const;

const mapPresetDetailTag = (presetId: string) =>
  ({ type: ApiTags.MapSettingPresets, id: presetId }) as const;

const mapSettingPresetsApi = createApi({
  reducerPath: "mapSettingPresetsApi",
  baseQuery: customFetchBase,
  tagTypes: [ApiTags.MapSettingPresets],
  endpoints: (builder) => ({
    getMapSettingPresets: builder.query<
      IMapSettingPresetListResponse,
      IGetMapSettingPresetsParams
    >({
      query: ({ districtId, page, limit }) => ({
        url: `/district/${districtId}/map-setting-presets`,
        method: "GET",
        params: { page, limit },
      }),
      providesTags: (result, error, { districtId }) => [mapPresetListTag(districtId)],
      transformResponse: (result: IApiResponse<IMapSettingPresetListResponse>) =>
        result.data,
    }),
    getMapSettingPreset: builder.query<
      IMapSettingPreset,
      { districtId: string; presetId: string }
    >({
      query: ({ districtId, presetId }) => ({
        url: `/district/${districtId}/map-setting-presets/${presetId}`,
        method: "GET",
      }),
      providesTags: (result, error, { presetId }) => [mapPresetDetailTag(presetId)],
      transformResponse: (result: IApiResponse<IMapSettingPreset>) => result.data,
    }),
    createMapSettingPreset: builder.mutation<
      IMapSettingPreset,
      { districtId: string; body: ICreateMapSettingPresetRequest }
    >({
      query: ({ districtId, body }) => ({
        url: `/district/${districtId}/map-setting-presets`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, { districtId }) => [mapPresetListTag(districtId)],
      transformResponse: (result: IApiResponse<IMapSettingPreset>) => result.data,
    }),
    updateMapSettingPreset: builder.mutation<
      IMapSettingPreset,
      {
        districtId: string;
        presetId: string;
        body: IUpdateMapSettingPresetRequest;
      }
    >({
      query: ({ districtId, presetId, body }) => ({
        url: `/district/${districtId}/map-setting-presets/${presetId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (result, error, { districtId, presetId }) => [
        mapPresetListTag(districtId),
        mapPresetDetailTag(presetId),
      ],
      transformResponse: (result: IApiResponse<IMapSettingPreset>) => result.data,
    }),
    deleteMapSettingPreset: builder.mutation<
      void,
      { districtId: string; presetId: string }
    >({
      query: ({ districtId, presetId }) => ({
        url: `/district/${districtId}/map-setting-presets/${presetId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { districtId, presetId }) => [
        mapPresetListTag(districtId),
        mapPresetDetailTag(presetId),
      ],
      transformResponse: () => undefined,
    }),
    setActiveMapSettingPreset: builder.mutation<
      IUserMapSettingPreset,
      { districtId: string; presetId: string }
    >({
      query: ({ districtId, presetId }) => ({
        url: `/district/${districtId}/map-setting-presets/${presetId}/active`,
        method: "PUT",
      }),
      invalidatesTags: (result, error, { districtId, presetId }) => [
        mapPresetListTag(districtId),
        mapPresetDetailTag(presetId),
      ],
      transformResponse: (result: IApiResponse<IUserMapSettingPreset>) =>
        result.data,
    }),
  }),
});

export const {
  useGetMapSettingPresetsQuery,
  useGetMapSettingPresetQuery,
  useLazyGetMapSettingPresetQuery,
  useCreateMapSettingPresetMutation,
  useUpdateMapSettingPresetMutation,
  useDeleteMapSettingPresetMutation,
  useSetActiveMapSettingPresetMutation,
} = mapSettingPresetsApi;

export type LazyGetMapSettingPresetTrigger = ReturnType<
  typeof useLazyGetMapSettingPresetQuery
>[0];

export default mapSettingPresetsApi;
